import {t} from "@lingui/core/macro";
import {lazy} from "react";

import {color} from "@khanacademy/wonder-blocks-tokens";

import {Layout, ToolCategories, type Tool} from "../types.ts";

import clipboardTextIcon from "@phosphor-icons/core/regular/clipboard-text.svg";

const CoeditingDocumentTool = lazy(
    () => import("../renderers/coediting-document-tool.tsx"),
);

export const rubricGenerator: Tool = {
    urlSlug: "rubric-generator",
    configName: "activity-create-a-rubric",
    title: () => t`Rubric generator`,
    description: () =>
        t`Design clear, detailed grading rubrics to set expectations and simplify scoring`,
    categories: [ToolCategories.Create, ToolCategories.Plan],
    icon: clipboardTextIcon,
    color: color.fadedGreen24,
    layout: Layout.Coeditor,
    extensionOrder: 4,
    activitySlug: "activity-create-a-rubric",
    reportingLabelId: "co-create-rubric",
    exportTargets: [
        "print",
        "download-pdf",
        "download-docx",
        "google-drive-docx",
        "microsoft-onedrive-docx",
    ],
    requestedInputs: [
        {
            description: () => "",
            inputKind: "SINGLE_SELECT",
            label: () => t`Grade level`,
            required: true,
            selectOptions: [
                {
                    label: () => t`1st grade`,
                    value: "1st grade",
                },
                {
                    label: () => t`2nd grade`,
                    value: "2nd grade",
                },
                {
                    label: () => t`3rd grade`,
                    value: "3rd grade",
                },
                {
                    label: () => t`4th grade`,
                    value: "4th grade",
                },
                {
                    label: () => t`5th grade`,
                    value: "5th grade",
                },
                {
                    label: () => t`6th grade`,
                    value: "6th grade",
                },
                {
                    label: () => t`7th grade`,
                    value: "7th grade",
                },
                {
                    label: () => t`8th grade`,
                    value: "8th grade",
                },
                {
                    label: () => t`9th grade`,
                    value: "9th grade",
                },
                {
                    label: () => t`10th grade`,
                    value: "10th grade",
                },
                {
                    label: () => t`11th grade`,
                    value: "11th grade",
                },
                {
                    label: () => t`12th grade`,
                    value: "12th grade",
                },
                {
                    label: () => t`College`,
                    value: "College",
                },
            ],
            variableName: "gradeLevel",
        },
        {
            description: () =>
                t`The more details you provide about the learning goals, the better. You may copy-paste the assignment instructions directly.`,
            inputKind: "TEXT_AREA",
            label: () => t`Assignment description`,
            required: true,
            variableName: "assignmentDescription",
        },
        {
            description: () =>
                t`List up to seven criteria or key competencies (e.g., Effective introduction). Or, leave this empty and Khanmigo will brainstorm criteria for you.`,
            inputKind: "DYNAMIC_TEXT_FIELDS",
            label: () => t`Rubric criteria (optional, one per line)`,
            maxFields: 7,
            variableName: "userInputtedCriteria",
        },
        {
            description: () =>
                t`How many performance levels would you like for each criterion?`,
            inputKind: "SINGLE_SELECT",
            label: () => t`Performance levels`,
            required: true,
            selectOptions: [
                {
                    label: () => t`3: Emerging, Progressing, Excelling`,
                    value: "3: Emerging, Progressing, Excelling",
                },
                {
                    label: () =>
                        t`4: Beginning Expectations, Developing Expectations, Meeting Expectations, Exceeding Expectations`,
                    value: "4: Beginning Expectations, Developing Expectations, Meeting Expectations, Exceeding Expectations",
                },
                {
                    label: () =>
                        t`5: Below Basic, Basic, Proficient, Approaching Advanced, Advanced`,
                    value: "5: Below Basic, Basic, Proficient, Approaching Advanced, Advanced",
                },
            ],
            variableName: "numPerformanceLevels",
        },
        {
            description: () =>
                t`What should we adjust about the overall rubric? (e.g.,use simple language.)`,
            inputKind: "TEXT_BOX",
            label: () => t`Overall notes`,
            variableName: "otherNotes",
        },
    ],
    component: ({providedInputs, configName, debugMode}) => {
        return (
            <CoeditingDocumentTool
                configName={configName}
                providedInputs={providedInputs}
                renderer="table"
                debugMode={debugMode}
            />
        );
    },
};
